import axios, { AxiosResponse } from 'axios';
import { convertRequestData, convertResponseData } from '../utils/apiHelpers';
import { getApiClientBaseUrl } from '../utils/apiConfig';

export const API_BASE_URL = getApiClientBaseUrl();

if (process.env.NODE_ENV === 'development') {
  // eslint-disable-next-line no-console
  console.log('API_BASE_URL:', API_BASE_URL);
}

export const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for debugging
authApi.interceptors.request.use(
  (config) => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('Auth API Request:', (config.baseURL || '') + (config.url || ''));
    }
    return config;
  },
  (error) => {
    console.error('Auth API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
authApi.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.log('Auth API Response:', response.status);
    }
    return response;
  },
  (error) => {
    console.error('Auth API Response Error:', {
      message: error.message,
      code: error.code,
      status: error.response?.status,
      url: error.config?.url,
    });
    return Promise.reject(error);
  }
);

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token and convert request data
api.interceptors.request.use(
  (config) => {
    // Try to get tokens from session first (new system)
    const sessionData = localStorage.getItem('session');
    if (sessionData) {
      try {
        const parsed = JSON.parse(sessionData);
        if (parsed.tokens && parsed.tokens.accessToken) {
          config.headers.Authorization = `Bearer ${parsed.tokens.accessToken}`;
        }
      } catch (error) {
        console.error('Error parsing session data:', error);
      }
    }
    
    // Fallback to old tokens storage (for backward compatibility)
    const tokens = localStorage.getItem('tokens');
    if (tokens) {
      try {
        const parsedTokens = JSON.parse(tokens);
        if (parsedTokens.accessToken) {
          config.headers.Authorization = `Bearer ${parsedTokens.accessToken}`;
        }
      } catch (error) {
        console.error('Error parsing tokens:', error);
      }
    }

    const facilityContext = localStorage.getItem('facilityContext');
    if (facilityContext) {
      config.headers['X-Facility-Id'] = facilityContext;
    }
    
    // For FormData uploads, drop JSON Content-Type so the browser sets multipart + boundary.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const headers = config.headers as { delete?: (name: string) => void };
      if (typeof headers?.delete === 'function') {
        headers.delete('Content-Type');
      } else {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }
    
    // Convert request data from camelCase to snake_case
    // Skip conversion for FormData (file uploads) and certain endpoints
    if (config.data && !(config.data instanceof FormData)) {
      const skipConversionPaths = ['/auth/login', '/auth/register', '/auth/refresh-token', '/daily-reports/'];
      const shouldSkip = skipConversionPaths.some(path => config.url?.includes(path));
      
      if (!shouldSkip) {
        config.data = convertRequestData(config.data);
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh and convert response data
api.interceptors.response.use(
  (response: AxiosResponse) => {
    // Skip conversion for binary (e.g. PDF download)
    if (response.data instanceof Blob) {
      return response;
    }
    // Convert response data from snake_case to camelCase
    if (response.data && typeof response.data === 'object') {
      // Skip conversion for auth endpoints
      const skipConversionPaths = ['/auth/login', '/auth/register', '/auth/refresh-token'];
      const shouldSkip = skipConversionPaths.some(path => response.config.url?.includes(path));
      
      if (!shouldSkip && response.data.data) {
        // Convert nested data object
        response.data.data = convertResponseData(response.data.data);
      } else if (!shouldSkip) {
        // Convert entire response if no nested data
        response.data = convertResponseData(response.data);
      }
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      // Check if the error is "Access token required" (no token provided)
      const errorMessage = error.response?.data?.message || '';
      const requiresAuth = error.response?.data?.requiresAuth || false;
      const redirectUrl = error.response?.data?.redirectUrl;
      
      // If no token was provided at all, redirect immediately
      if (errorMessage.includes('Access token required') || requiresAuth) {
        // Clear any existing tokens/session
        localStorage.removeItem('tokens');
        localStorage.removeItem('session');
        
        // Redirect to login (use provided redirectUrl or default)
        const loginUrl = redirectUrl || '/login';
        if (redirectUrl && redirectUrl.startsWith('http')) {
          window.location.href = redirectUrl;
        } else {
          window.location.href = loginUrl;
        }
        return Promise.reject(error);
      }
      
      // Otherwise, try to refresh the token
      originalRequest._retry = true;

      try {
        const tokens = localStorage.getItem('tokens');
        if (tokens) {
          const parsedTokens = JSON.parse(tokens);
          const response = await authApi.post('/auth/refresh-token', {
            refreshToken: parsedTokens.refreshToken
          });

          if (response.data.success) {
            const newTokens = {
              ...parsedTokens,
              accessToken: response.data.data.accessToken
            };
            
            localStorage.setItem('tokens', JSON.stringify(newTokens));
            api.defaults.headers.common['Authorization'] = `Bearer ${newTokens.accessToken}`;
            
            return api(originalRequest);
          }
        }
      } catch (refreshError) {
        console.error('Token refresh failed:', refreshError);
        localStorage.removeItem('tokens');
        localStorage.removeItem('session');
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

export default api;
